import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { Badge } from '@teals/ui-web';

/* ===================================================================
   STYLED COMPONENTS
   =================================================================== */

const TreeRoot = styled.div`
  font-family: 'SF Mono', 'Fira Code', 'Monaco', monospace;
  font-size: 0.75rem;
  line-height: 1.5;
  color: var(--text-secondary);
`;

const TreeNode = styled.div<{ $depth: number }>`
  padding-left: ${({ $depth }) => $depth * 16}px;
  position: relative;
  min-height: 1.5em;

  &::before {
    content: '';
    position: absolute;
    left: ${({ $depth }) => $depth * 16 - 8}px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: var(--border);
    opacity: 0.5;
  }
`;

const TreeNodeLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  padding: 0.125rem 0;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: var(--bg-elevated);
  }
`;

const ToggleBtn = styled.span`
  width: 14px;
  flex-shrink: 0;
  color: var(--text-muted);
  user-select: none;
  font-size: 0.625rem;
`;

const KeyName = styled.span`
  color: var(--accent);
  flex-shrink: 0;
`;

const TypeBadge = styled(Badge)<{ $variant: string }>`
  font-size: 0.5625rem;
  padding: 0.0625rem 0.375rem;
  flex-shrink: 0;
`;

const ValueText = styled.span<{ $type: string }>`
  color: ${({ $type }) =>
    $type === 'string'
      ? 'var(--success)'
      : $type === 'number'
        ? 'var(--warning)'
        : $type === 'boolean'
          ? 'var(--info)'
          : $type === 'null'
            ? 'var(--text-muted)'
            : 'var(--text-secondary)'};
  word-break: break-word;
`;

const CopyHint = styled.span`
  font-size: 0.5625rem;
  color: var(--text-muted);
  opacity: 0;
  margin-left: auto;
  transition: opacity 0.15s;

  ${TreeNodeLine}:hover & {
    opacity: 1;
  }
`;

/* ===================================================================
   COMPONENT
   =================================================================== */

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonObject | JsonArray;
interface JsonObject {
  [key: string]: JsonValue;
}
type JsonArray = JsonValue[];

interface JsonTreeViewProps {
  data: JsonValue;
  maxDepth?: number;
}

const TYPE_COLORS: Record<string, 'primary' | 'success' | 'warning' | 'info'> = {
  string: 'success',
  number: 'warning',
  boolean: 'info',
  null: 'primary',
  array: 'primary',
  object: 'primary',
};

function getType(value: JsonValue): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  return typeof value;
}

function formatValue(value: JsonPrimitive): string {
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  return String(value);
}

export const JsonTreeView: React.FC<JsonTreeViewProps> = ({
  data,
  maxDepth = 10,
}) => {
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set(['']));

  const togglePath = useCallback((path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const copyToClipboard = useCallback((text: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(text).catch(() => {});
  }, []);

  const renderNode = (value: JsonValue, path: string, depth: number, keyLabel?: string): React.ReactNode => {
    if (depth > maxDepth) {
      return <ValueText $type="string">…</ValueText>;
    }

    const type = getType(value);

    if (type === 'null' || type === 'string' || type === 'number' || type === 'boolean') {
      const str = formatValue(value as JsonPrimitive);
      const display = type === 'string' ? `"${str}"` : str;
      return (
        <TreeNodeLine
          onClick={(e) => copyToClipboard(JSON.stringify(value), e)}
          title="Click to copy"
        >
          {keyLabel != null && (
            <>
              <KeyName>&quot;{keyLabel}&quot;</KeyName>:{' '}
            </>
          )}
          <TypeBadge variant={TYPE_COLORS[type] as 'primary'} $variant={type} size="sm">
            {type}
          </TypeBadge>
          <ValueText $type={type}>{display}</ValueText>
          <CopyHint>copy</CopyHint>
        </TreeNodeLine>
      );
    }

    if (type === 'array') {
      const arr = value as JsonArray;
      const isExpanded = expandedPaths.has(path);
      const label = `array[${arr.length}]`;

      return (
        <div>
          <TreeNodeLine onClick={() => togglePath(path)}>
            {keyLabel != null && (
              <>
                <KeyName>&quot;{keyLabel}&quot;</KeyName>:{' '}
              </>
            )}
            <ToggleBtn>{isExpanded ? '▾' : '▸'}</ToggleBtn>
            <TypeBadge variant="primary" $variant="array" size="sm">
              {label}
            </TypeBadge>
            <CopyHint>expand</CopyHint>
          </TreeNodeLine>
          {isExpanded &&
            arr.map((item, i) => (
              <TreeNode key={`${path}[${i}]`} $depth={depth + 1}>
                {renderNode(item, `${path}[${i}]`, depth + 1, String(i))}
              </TreeNode>
            ))}
        </div>
      );
    }

    if (type === 'object') {
      const obj = value as JsonObject;
      const keys = Object.keys(obj);
      const isExpanded = expandedPaths.has(path);
      const label = `object{${keys.length}}`;

      return (
        <div>
          <TreeNodeLine onClick={() => togglePath(path)}>
            {keyLabel != null && (
              <>
                <KeyName>&quot;{keyLabel}&quot;</KeyName>:{' '}
              </>
            )}
            <ToggleBtn>{isExpanded ? '▾' : '▸'}</ToggleBtn>
            <TypeBadge variant="primary" $variant="object" size="sm">
              {label}
            </TypeBadge>
            <CopyHint>expand</CopyHint>
          </TreeNodeLine>
          {isExpanded &&
            keys.map((k) => (
              <TreeNode key={`${path}.${k}`} $depth={depth + 1}>
                {renderNode(obj[k], `${path}.${k}`, depth + 1, k)}
              </TreeNode>
            ))}
        </div>
      );
    }

    return null;
  };

  return (
    <TreeRoot>
      {renderNode(data, '', 0)}
    </TreeRoot>
  );
};

export default JsonTreeView;
